import { useMemo } from "react";

type ParentTree = {
  folder1: string;
  children?: CampaignNode[];
};

type CampaignNode = {
  name: string;
  status: string;
  Destinations: string;
  type: "folder" | "campaign";
  cadence: string;
  createdby: string;
  creation_time: string;
  lastEditedBy: string;
  last_modified_time: string;
  count: number;
  campaigns: string[];
  children?: CampaignNode[];
};

type UseFilteredCampaignsParams = {
  status: string[];
  cadence: string[];
  destination: string[];
  createdBy: string[];
  lastEditedBy: string[];
};

export const useFilteredCampaigns = (
  { parseDataCampaignDropdown, watch }: any,
  filters: UseFilteredCampaignsParams
) => {
  const { status, cadence, destination, createdBy, lastEditedBy } = filters;
  const rawAudienceTree = useMemo(() => {
    if (!Array.isArray(parseDataCampaignDropdown)) return [];
    return parseDataCampaignDropdown as ParentTree[];
  }, [parseDataCampaignDropdown]);

  function flattenCampaigns(nodes: any[] | undefined): any[] {
    if (!nodes) return [];
    return nodes.flatMap((node) => {
      return node.type === "campaign" ? [node] : flattenCampaigns(node.children);
    });
  }

  const flatAudienceList = rawAudienceTree.flatMap((folder1) => flattenCampaigns(folder1.children));

  const getUniqueOptions = (key: keyof CampaignNode): string[] => {
    const unique = new Set<string>();
    flatAudienceList.forEach((item) => {
      if (item[key]) {
        unique.add(item[key] as string);
      }
    });
    return ["All", ...Array.from(unique).sort()];
  };

  const statusOptions = getUniqueOptions("status");
  const cadenceOptions = getUniqueOptions("cadence");
  const destinationOptions = Array.from(new Set(getUniqueOptions("Destinations").flat()));
  const createdByOptions = getUniqueOptions("createdby");
  const lastEditedByOptions = getUniqueOptions("lastEditedBy");

  const inputNameValue = watch("search");

  const filteredData = useMemo(() => {
    if (
      !inputNameValue.trim() &&
      destination.includes("All") &&
      status.includes("All") &&
      cadence.includes("All") &&
      createdBy.includes("All") &&
      lastEditedBy.includes("All")
    )
      return rawAudienceTree;

    const filterCampaigns = (nodes?: CampaignNode[]): CampaignNode[] => {
      if (!nodes) return [];

      return nodes
        .map((node) => {
          const matchesName =
            !inputNameValue.trim() ||
            node.name?.toLowerCase().includes(inputNameValue.trim().toLowerCase());

          const matchesDestination =
            destination.includes("All") || destination.includes(node.Destinations?.[0]);

          const matchesStatus = status.includes("All") || status.includes(node.status);

          const matchesCadence = cadence.includes("All") || cadence.includes(node.cadence);

          const matchesCreatedBy = createdBy.includes("All") || createdBy.includes(node.createdby);

          const matchesLastEditedBy =
            lastEditedBy.includes("All") || lastEditedBy.includes(node.lastEditedBy);

          const thisNodeMatches =
            matchesName &&
            matchesDestination &&
            matchesStatus &&
            matchesCadence &&
            matchesCreatedBy &&
            matchesLastEditedBy;

          const filteredChildren = filterCampaigns(node.children);

          if (thisNodeMatches || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        })
        .filter(Boolean) as CampaignNode[];
    };

    return rawAudienceTree
      .map((folder1) => ({
        ...folder1,
        children: filterCampaigns(folder1.children),
      }))
      .filter((folder1) => folder1.children && folder1.children.length > 0);
  }, [inputNameValue, destination, status, cadence, createdBy, lastEditedBy, rawAudienceTree]);

  

  return {
    inputNameValue,
    status,
    cadence,
    destination,
    createdBy,
    lastEditedBy,
    statusOptions,
    cadenceOptions,
    destinationOptions,
    createdByOptions,
    lastEditedByOptions,
    rawAudienceTree,
    filteredData,
  };
};
